import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import CircularProgress from '@material-ui/core/CircularProgress'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'

import { Main, PageHeader } from '../components/Layout'
import { constants, dates } from '../utils'
import { appService, waitingListService } from '../services'

const useStyles = makeStyles(() => ({
  center: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  margin: {
    marginTop: '12px',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    width: '100%',
    padding: '30px',
  },
  paper: {
    padding: '24px',
    marginTop: '16px',
  },
  content: {
    marginTop: '16px',
  },
  formControl: {
    width: '100%',
    margin: '8px 0',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
  },
  table: {
    marginTop: '16px',
  },
  tableCell: {
    fontWeight: 'bold',
  },
  emptyMessage: {
    padding: '20px',
    textAlign: 'center',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    marginBottom: '8px',
  },
  lastUpdated: {
    whiteSpace: 'nowrap',
    border: '1px solid',
    borderRadius: '15px',
    padding: '4px 6px',
  },
}))

const WaitingList = () => {
  const classes = useStyles()
  const history = useHistory()

  const publicCall = appService.getLocalPublicCall()

  const [loading, setLoading] = useState(false)
  const [cities, setCities] = useState([])
  const [schools, setSchools] = useState([])
  const [offers, setOffers] = useState([])
  const [waitingList, setWaitingList] = useState([])
  const [consulting, setConsulting] = useState(false)
  const [selectedOfferData, setSelectedOfferData] = useState(null)
  const [enableConsultingButton, setEnableConsultingButton] = useState(true)

  const [selectedCity, setSelectedCity] = useState('')
  const [selectedSchool, setSelectedSchool] = useState('')
  const [selectedOffer, setSelectedOffer] = useState('')
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)

  // Buscar cidades ao carregar
  useEffect(() => {
    const fetchCities = async () => {
      setLoading(true)
      const response = await waitingListService.getAllCities()
      if (response.success) {
        setCities(response.payload.cities)
      }
      setLoading(false)
    }

    fetchCities()
  }, [])

  // Buscar escolas (todas ou filtradas por cidade)
  useEffect(() => {
    const fetchSchools = async () => {
      setLoading(true)
      setOffers([])
      setSelectedSchool('')
      setSelectedOffer('')

      const response = await waitingListService.getSchools(selectedCity || null)
      if (response.success) {
        setSchools(response.payload.schools)
      }
      setLoading(false)
    }

    fetchSchools()
  }, [selectedCity])

  // Buscar ofertas quando selecionar escola
  useEffect(() => {
    if (!selectedSchool) {
      setOffers([])
      setSelectedOffer('')
      return
    }

    const fetchOffers = async () => {
      setLoading(true)
      setSelectedOffer('')

      const response = await waitingListService.getOffers(
        selectedSchool,
        selectedCity || null,
      )
      if (response.success) {
        setOffers(response.payload.offers)
      }
      setLoading(false)
    }

    fetchOffers()
  }, [selectedSchool, selectedCity])

  const handleConsult = async () => {
    if (!selectedOffer) return

    setConsulting(true)
    setWaitingList([])
    setLastUpdatedAt(null)

    const response = await waitingListService.getWaitingList(selectedOffer)

    if (response.success) {
      setWaitingList(response.payload.waitingList)
      const offerData = offers.find(o => o.id === parseInt(selectedOffer))
      setSelectedOfferData(offerData)
      setLastUpdatedAt(dates.now('DD/MM/YYYY HH:mm:ss'))

      setEnableConsultingButton(false)
    }

    setConsulting(false)
  }

  const handleClearFilters = () => {
    setSelectedCity('')
    setSelectedSchool('')
    setSelectedOffer('')
    setOffers([])
    setWaitingList([])
    setSelectedOfferData(null)
    setLastUpdatedAt(null)
  }

  const canConsult = selectedSchool && selectedOffer
  const hasFilters = selectedCity || selectedSchool || selectedOffer

  return (
    <>
      <PageHeader title="Lista de espera" />

      <Main className={classes.main}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" component="h2">
              Consultar lista de espera
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: '8px' }}
            >
              Selecione o município, escola e oferta para consultar a lista de
              espera
            </Typography>
          </Grid>

          {publicCall.waitingListInstructions && (
            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <div
                  className={classes.content}
                  dangerouslySetInnerHTML={{
                    __html: publicCall.waitingListInstructions,
                  }}
                />
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    disabled={loading || !cities.length}
                  >
                    <InputLabel>Município *</InputLabel>
                    <Select
                      value={selectedCity}
                      onChange={e => setSelectedCity(e.target.value)}
                      label="Município *"
                    >
                      {cities.map(city => (
                        <MenuItem key={city.value} value={city.value}>
                          {city.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    disabled={loading || !schools.length}
                  >
                    <InputLabel>Escola *</InputLabel>
                    <Select
                      value={selectedSchool}
                      onChange={e => setSelectedSchool(e.target.value)}
                      label="Escola *"
                    >
                      {schools.map(school => (
                        <MenuItem key={school.value} value={school.value}>
                          {school.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    disabled={loading || !selectedSchool || !offers.length}
                  >
                    <InputLabel>Oferta *</InputLabel>
                    <Select
                      value={selectedOffer}
                      onChange={e => setSelectedOffer(e.target.value)}
                      label="Oferta *"
                    >
                      {offers.map(offer => (
                        <MenuItem key={offer.id} value={offer.id}>
                          {offer.label || `Oferta ${offer.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {loading && (
                  <Grid item xs={12} className={classes.loading}>
                    <CircularProgress size={24} />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                padding: '20px 0',
              }}
            >
              <Button
                variant="contained"
                color="default"
                onClick={() => history.push(constants.routes.home.path)}
              >
                Voltar
              </Button>
              <Button
                variant="outlined"
                color="default"
                disabled={!hasFilters || loading}
                onClick={handleClearFilters}
              >
                Limpar Filtros
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!canConsult || loading || consulting}
                onClick={handleConsult}
              >
                {consulting ? 'Consultando...' : 'Consultar'}
              </Button>

              <Button
                variant="contained"
                color="primary"
                disabled={enableConsultingButton}
                onClick={() =>
                  history.push(
                    `${constants.routes.waitingList.requestInclusion.path}?offerId=${selectedOffer}&schoolId=${selectedSchool}`,
                  )
                }
              >
                Solicitar Inclusão na Fila de Espera
              </Button>
            </div>
          </Grid>

          {selectedOfferData && (
            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <div className={classes.titleRow}>
                  <Typography variant="h6" component="h3">
                    Fila de Espera
                  </Typography>
                  {lastUpdatedAt && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.lastUpdated}
                    >
                      Última atualização: {lastUpdatedAt}
                    </Typography>
                  )}
                </div>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {selectedOfferData.schoolName && (
                    <>Escola: {selectedOfferData.schoolName}</>
                  )}
                  {selectedOfferData.cityName && (
                    <> | Cidade: {selectedOfferData.cityName}</>
                  )}
                  {selectedOfferData.label && (
                    <> | Oferta: {selectedOfferData.label}</>
                  )}
                </Typography>
                {waitingList.length > 0 ? (
                  <TableContainer className={classes.table}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableCell}>
                            Posição
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            Aluno
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            Situação
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            Prioridade Legal
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {waitingList.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>{item.position || '--'}</TableCell>
                            <TableCell>{item.studentName || '--'}</TableCell>
                            <TableCell>{item.situation || '--'}</TableCell>
                            <TableCell>
                              {item.legalPriorityGroupName || '--'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  !consulting && (
                    <Typography className={classes.emptyMessage}>
                      Nenhum candidato encontrado na fila de espera para esta
                      oferta
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
          )}

          {consulting && (
            <Grid item xs={12} className={classes.loading}>
              <CircularProgress size={24} />
            </Grid>
          )}

          {!consulting &&
            waitingList.length === 0 &&
            selectedOfferData === null &&
            selectedOffer && (
              <Grid item xs={12}>
                <Paper className={classes.paper}>
                  <Typography className={classes.emptyMessage}>
                    Clique em "Consultar" para visualizar a fila de espera
                  </Typography>
                </Paper>
              </Grid>
            )}
        </Grid>
      </Main>
    </>
  )
}

export default WaitingList
